/**
 * AdditionalFieldsEditor Component
 *
 * Unified component for displaying and editing additional fields.
 * Merges customFields and jsonAdditionalConfig into a single view.
 */

import React, { useState, useMemo } from 'react';
import { CustomField } from '../../types/configTemplate';
import { AdditionalFieldModal } from './AdditionalFieldModal';
import { useI18n } from '../../contexts/I18nContext';
import {
  AdditionalField,
  mergeAdditionalFields,
  formatValueForDisplay,
  getTypeBadgeColor,
  getSourceBadgeColor,
} from '../../utils/additionalFieldsUtils';
import { hasSecret, maskSecrets } from '../../utils/secrets';
import './AdditionalFieldsEditor.css';

interface AdditionalFieldsEditorProps {
  customFields: CustomField[];
  jsonAdditionalConfig: Record<string, unknown> | Array<string> | null;
  onChange: (fields: CustomField[]) => void;
  isEditing: boolean;
  templateFields?: CustomField[];  // Fields from template for source tracking
  /** Mask every password / secret the values carry (a client's members); a masked field cannot be edited. */
  maskSecrets?: boolean;
}

export const AdditionalFieldsEditor: React.FC<AdditionalFieldsEditorProps> = ({
  customFields,
  jsonAdditionalConfig,
  onChange,
  isEditing,
  templateFields,
  maskSecrets: masked = false,
}) => {
  const { t } = useI18n();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingField, setEditingField] = useState<AdditionalField | null>(null);
  const [expandedJsonFields, setExpandedJsonFields] = useState<Set<string>>(new Set());

  // Merge all fields into a unified view
  const mergedFields = useMemo(() => {
    return mergeAdditionalFields(customFields, jsonAdditionalConfig, templateFields);
  }, [customFields, jsonAdditionalConfig, templateFields]);

  const handleAddField = (field: AdditionalField) => {
    const newCustomFields = [...customFields, {
      name: field.name,
      value: typeof field.value === 'object' ? JSON.stringify(field.value) : String(field.value),
    }];
    onChange(newCustomFields);
  };

  const handleEditField = (field: AdditionalField) => {
    // Find if this field exists in customFields
    const existingIndex = customFields.findIndex(f => f.name === field.name);
    const newValue = typeof field.value === 'object' ? JSON.stringify(field.value) : String(field.value);

    if (existingIndex >= 0) {
      // Update existing custom field
      const newCustomFields = [...customFields];
      newCustomFields[existingIndex] = { name: field.name, value: newValue };
      onChange(newCustomFields);
    } else {
      // This was a device/template field, add as override
      onChange([...customFields, { name: field.name, value: newValue }]);
    }
  };

  const handleDeleteField = (name: string) => {
    // Only delete from customFields - device fields can't be deleted, only overridden
    onChange(customFields.filter(f => f.name !== name));
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setEditingField(null);
  };

  const handleModalSave = (field: AdditionalField) => {
    if (editingField) {
      handleEditField(field);
    } else {
      handleAddField(field);
    }
  };

  const toggleJsonExpand = (fieldName: string) => {
    setExpandedJsonFields(prev => {
      const next = new Set(prev);
      if (next.has(fieldName)) {
        next.delete(fieldName);
      } else {
        next.add(fieldName);
      }
      return next;
    });
  };

  const existingNames = mergedFields.map(f => f.name);

  // Determine if a field can be deleted (only user-added fields)
  const canDelete = (field: AdditionalField) => {
    return customFields.some(f => f.name === field.name);
  };

  const canEdit = (field: AdditionalField) => !(masked && hasSecret(field.value, field.name));

  const renderValue = (rawField: AdditionalField) => {
    const field = masked ? { ...rawField, value: maskSecrets(rawField.value, rawField.name) } : rawField;
    const isExpanded = expandedJsonFields.has(field.name);

    if (field.valueType === 'json') {
      const formatted = formatValueForDisplay(field.value, field.valueType);
      const isLong = formatted.length > 50 || formatted.includes('\n');

      if (isLong) {
        return (
          <div className="json-value-container">
            <button
              type="button"
              className="json-toggle"
              onClick={() => toggleJsonExpand(field.name)}
            >
              {isExpanded ? '▼' : '▶'} {isExpanded ? 'Collapse' : 'Expand'}
            </button>
            {isExpanded && (
              <pre className="json-value-expanded">{formatted}</pre>
            )}
            {!isExpanded && (
              <code className="json-value-preview">
                {formatted.substring(0, 50)}...
              </code>
            )}
          </div>
        );
      }
      return <code className="json-value-inline">{formatted}</code>;
    }

    if (field.valueType === 'array') {
      const formatted = formatValueForDisplay(field.value, field.valueType);
      return <span className="array-value">[{formatted}]</span>;
    }

    return <span>{formatValueForDisplay(field.value, field.valueType)}</span>;
  };

  return (
    <div className="additional-fields-editor">
      {isEditing && (
        <div className="editor-actions">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => setShowAddModal(true)}
            type="button"
          >
            + Add Field
          </button>
        </div>
      )}

      {mergedFields.length === 0 ? (
        <div className="empty-state">
          {t('devices.additionalFieldsSection')}: —
        </div>
      ) : (
        <table className="additional-fields-table">
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              <th>{t('common.value')}</th>
              <th>{t('common.type')}</th>
              <th>{t('common.source')}</th>
              {isEditing && <th>{t('common.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {mergedFields.map(field => (
              <tr key={field.name}>
                <td className="field-name">
                  <code>{field.name}</code>
                </td>
                <td className="field-value">
                  {renderValue(field)}
                </td>
                <td className="field-type">
                  <span
                    className="type-badge"
                    style={{ backgroundColor: getTypeBadgeColor(field.valueType) }}
                  >
                    {field.valueType}
                  </span>
                </td>
                <td className="field-source">
                  <span
                    className="source-badge"
                    style={{ backgroundColor: getSourceBadgeColor(field.source) }}
                  >
                    {field.source}
                  </span>
                </td>
                {isEditing && (
                  <td className="field-actions">
                    {canEdit(field) && (
                    <button
                      className="btn-icon"
                      onClick={() => setEditingField(field)}
                      title="Edit"
                      type="button"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    )}
                    {canDelete(field) && (
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => handleDeleteField(field.name)}
                        title="Delete"
                        type="button"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <AdditionalFieldModal
        isOpen={showAddModal || editingField !== null}
        field={editingField}
        onClose={handleModalClose}
        onSave={handleModalSave}
        existingNames={editingField ? existingNames.filter(n => n !== editingField.name) : existingNames}
      />
    </div>
  );
};

export default AdditionalFieldsEditor;
